import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Tooltip,
} from '@mui/material';
import { Plus, FileText, Calendar, ClipboardList } from 'lucide-react';

import { useAppDispatch, useAppSelector } from '@/store';
import { openModal } from '@/store/slices/uiSlice';
import { selectCurrentUser } from '@/store/slices/authSlice';
import { isAdminOrAbove } from '@/shared/utils/roles';

/**
 * QuickActionsWidget
 * Common actions with single-click access
 * Action-first UI principle
 */
const QuickActionsWidget: React.FC = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectCurrentUser);
  const canCreateProject = isAdminOrAbove(user?.role);

  const actions: Array<{
    icon: typeof Plus;
    label: string;
    onClick?: () => void;
    disabled?: boolean;
    tooltip?: string;
  }> = [
    {
      icon: Plus,
      label: 'New Task',
      onClick: () => dispatch(openModal({ type: 'taskCreate' })),
    },
    ...(canCreateProject
      ? [{
          icon: FileText,
          label: 'New Project',
          onClick: () => dispatch(openModal({ type: 'projectCreate' })),
        }]
      : []),
    {
      icon: Calendar,
      label: 'Request Leave',
      onClick: () => dispatch(openModal({ type: 'leaveRequest' })),
    },
    {
      icon: ClipboardList,
      label: 'Log Time',
      disabled: true,
      tooltip: 'Coming soon',
    },
  ];

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
          Quick Actions
        </Typography>

        <Grid container spacing={1}>
          {actions.map((action) => {
            const Icon = action.icon;
            const button = (
              <Button
                fullWidth
                variant="contained"
                disableElevation
                disabled={action.disabled}
                onClick={action.onClick}
                sx={{
                  py: 1.5,
                  flexDirection: 'column',
                  gap: 0.5,
                  bgcolor: '#4F46E5',
                  color: '#ffffff',
                  '&:hover': action.disabled
                    ? undefined
                    : { bgcolor: '#2c2e32', color: '#ffffff' },
                  '&.Mui-disabled': {
                    bgcolor: '#4F46E5',
                    color: '#ffffff',
                    opacity: 0.45,
                  },
                }}
              >
                <Icon size={20} color="#ffffff" />
                <Typography variant="caption" sx={{ fontWeight: 500, color: '#ffffff' }}>
                  {action.label}
                </Typography>
              </Button>
            );

            return (
              <Grid item xs={6} key={action.label}>
                {action.tooltip ? (
                  <Tooltip title={action.tooltip}>
                    <Box component="span" sx={{ display: 'block', width: '100%' }}>
                      {button}
                    </Box>
                  </Tooltip>
                ) : (
                  button
                )}
              </Grid>
            );
          })}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default QuickActionsWidget;
